import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminApplicants() {
  const { user, role } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [assignedHiringIds, setAssignedHiringIds] = useState(null); // null = no restriction

  useEffect(() => {
    (async () => {
      try {
        let hasAssignment = false;
        let assignedJobIds = [];

        if (role !== "superadmin" && user) {
          const asnap = await getDoc(doc(db, "admin_assignments", user.uid));
          if (asnap.exists() && (asnap.data().assignedJobs || []).length > 0) {
            hasAssignment = true;
            assignedJobIds = asnap.data().assignedJobs;
            setAssignedHiringIds(assignedJobIds);
          }
        }

        const [applicantSnap, hiringSnap] = await Promise.all([
          getDocs(query(collection(db, "applicants"), orderBy("createdAt", "desc"))),
          hasAssignment ? getDocs(collection(db, "hirings")) : Promise.resolve(null)
        ]);

        let all = applicantSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (hasAssignment) {
          // Build a set of hiring titles assigned to this admin (for legacy fallback)
          const hiringTitles = hiringSnap
            ? hiringSnap.docs
                .filter((d) => assignedJobIds.includes(d.id))
                .map((d) => d.data().title)
            : [];

          all = all.filter((a) => {
            // Explicitly assigned to this admin
            if (a.assignedAdminId) return a.assignedAdminId === user.uid;
            // Legacy / pending: no assignedAdminId yet — match by job title
            if ((a.selectedJobs || []).some((t) => hiringTitles.includes(t))) return true;
            // Unassigned pending applicants (no jobs selected yet) — show to all admins
            return !a.selectedJobs || a.selectedJobs.length === 0;
          });
        }

        setItems(all);
      } catch (e) { console.error(e); }
    })();
  }, [user, role]);

  const filtered = items.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.firstName?.toLowerCase().includes(s) || a.lastName?.toLowerCase().includes(s) || a.applicationId?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s));
    }
    return true;
  });

  return (
    <AdminShell title="Applicants">
      {assignedHiringIds && (
        <div className="alert alert-info mb-16" style={{ fontSize: 13 }}>
          <i className="ti ti-git-branch"></i> Showing applicants assigned to your jobs only. Contact a superadmin to change your assignment.
        </div>
      )}
      <div className="card">
        <div className="flex-between mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
          <input className="form-control" style={{ maxWidth: 320 }} placeholder="Search name, email, ID..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <div style={{ display: "flex", gap: 6 }}>
            {["all", "pending", "accepted", "rejected"].map((f) => (
              <button key={f} className={"btn " + (filter === f ? "btn-primary" : "btn-outline")} onClick={() => setFilter(f)}>
                {f}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state"><i className="ti ti-search"></i><div>No matching applicants</div></div>
        ) : (
          <div className="table-wrap"><table className="table">
            <thead>
              <tr><th>Endorsement ID</th><th>Name / Age</th><th>Contact</th><th>Status</th><th>Phase</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const age = a.birthdate ? Math.floor((Date.now() - new Date(a.birthdate)) / 3.156e10) : null;
                const addr = [a.address?.city, a.address?.province].filter(Boolean).join(", ");
                const needsAction =
                  a.status === "pending" ||
                  a.endorsementStatus === "ready_to_claim" ||
                  a.phase2Status === "ready_to_claim";
                return (
                  <tr key={a.id} style={{ background: needsAction ? "#fff8e8" : undefined }}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {a.applicationId}
                      {needsAction && <span className="badge badge-pending" style={{ marginLeft: 6, fontSize: 10 }}>Action</span>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.firstName} {a.lastName}</div>
                      <div style={{ fontSize: 12, color: "var(--ink-400)" }}>
                        {age && <span>Age {age}</span>}
                        {addr && <span style={{ marginLeft: 6 }}>· {addr}</span>}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div>{a.contactNumber}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-400)" }}>{a.email}</div>
                    </td>
                    <td><span className={`badge badge-${a.status}`}>{a.status}</span></td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {a.endorsementStatus && (
                          <span className={`badge ${a.endorsementStatus === "for_aboard" ? "badge-ready" : a.endorsementStatus === "claimed" ? "badge-claimed" : a.endorsementStatus === "ready_to_claim" ? "badge-ready" : "badge-processing"}`} style={{ fontSize: 10 }}>
                            P1: {a.endorsementStatus.replace(/_/g, " ")}
                          </span>
                        )}
                        {a.phase2Status && (
                          <span className={`badge ${a.phase2Status === "claimed" ? "badge-claimed" : a.phase2Status === "ready_to_claim" ? "badge-ready" : "badge-processing"}`} style={{ fontSize: 10 }}>
                            P2: {a.phase2Status.replace(/_/g, " ")}
                          </span>
                        )}
                        {a.endorsementStatus === "for_aboard" && (
                          <span className="badge badge-accepted" style={{ fontSize: 10 }}><i className="ti ti-plane"></i> Aboard</span>
                        )}
                        {!a.endorsementStatus && !a.phase2Status && <span style={{ fontSize: 12, color: "var(--ink-300)" }}>—</span>}
                      </div>
                    </td>
                    <td><Link to={`/admin/applicants/${a.id}`} className="btn btn-outline" style={{ fontSize: 12, padding: "5px 10px" }}><i className="ti ti-eye"></i> View</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </AdminShell>
  );
}

